import { SHEET, slotPosition } from "./labelSheet";

// Unidade do react-pdf é o ponto (1/72 pol.)
const MM = 72 / 25.4;
const PAD = 1.2 * MM;
const LABEL_WIDTH = 38.1 * MM;

/**
 * Maior fonte (pt) em que o nome cabe em `lines` linhas de `width` pt.
 * Estimativa pela largura média do Helvetica-Bold (~0,56 em), com folga
 * para a quebra por palavras; a altura do bloco ainda é limitada no layout.
 */
const fitNameSize = (name, width, lines) =>
  [8.5, 8, 7.5, 7, 6.5, 6, 5.5].find(
    (size) => Math.ceil((name.length * size * 0.56 * 1.12) / width) <= lines
  ) || 5.5;

/**
 * Gera o PDF (Blob) das folhas montadas por `buildSheets`. Cada etiqueta é
 * posicionada em mm a partir do canto da folha, somando a calibração do layout.
 * As bibliotecas são carregadas sob demanda (só quando o usuário imprime).
 */
export async function createLabelsPdf(pages, layout) {
  const [pdfLib, qrLib] = await Promise.all([
    import("@react-pdf/renderer"),
    import("qrcode"),
  ]);
  const { Document, Page, View, Text, Image, StyleSheet, Font, pdf } = pdfLib;
  const QRCode = qrLib.default || qrLib;

  // A hifenização padrão (inglês) quebra palavras em pt-BR
  Font.registerHyphenationCallback((word) => [word]);

  // Um PNG por conteúdo distinto (a mesma etiqueta se repete muitas vezes)
  const qrTexts = new Set(pages.flat().map((slot) => slot?.label?.qr).filter(Boolean));
  const qrImages = new Map();
  await Promise.all(
    [...qrTexts].map(async (text) => {
      qrImages.set(
        text,
        await QRCode.toDataURL(text, { margin: 0, width: 256, errorCorrectionLevel: "L" })
      );
    })
  );

  const styles = StyleSheet.create({
    page: { backgroundColor: "#ffffff" },
    label: {
      position: "absolute",
      width: SHEET.labelWidth * MM,
      height: SHEET.labelHeight * MM,
      padding: PAD,
      color: "#000000",
    },
    guide: { borderWidth: 0.5, borderStyle: "dashed", borderColor: "#9ca3af", borderRadius: 3 },
    body: { flexDirection: "row", alignItems: "center", flexGrow: 1 },
    info: { flex: 1, justifyContent: "center", paddingRight: 2 },
    name: {
      fontFamily: "Helvetica-Bold",
      lineHeight: 1.1,
    },
    line: { fontFamily: "Helvetica", fontSize: 6.5, lineHeight: 1.15, textOverflow: "ellipsis" },
    validity: { fontFamily: "Helvetica-Bold" },
    note: {
      fontFamily: "Helvetica",
      fontSize: 5.5,
      lineHeight: 1.15,
      marginTop: 1,
      paddingTop: 1,
      borderTopWidth: 0.4,
      borderTopColor: "#000000",
      textOverflow: "ellipsis",
    },
  });

  const renderLabel = ({ name, fabLine, valLine, note, qr }) => {
    const qrSize = note ? 36 : 44;
    const lines = note ? 2 : 3;
    const textWidth = LABEL_WIDTH - 2 * PAD - (qr ? qrSize + 2 : 0);
    const nameSize = fitNameSize(name, textWidth, lines);
    return (
      <>
        <View style={styles.body}>
          <View style={styles.info}>
            {/* maxLines do react-pdf não é confiável: a altura garante o limite de linhas */}
            <View
              style={{
                maxHeight: lines * nameSize * 1.1 + 0.5,
                marginBottom: 1.5,
                overflow: "hidden",
              }}
            >
              <Text style={[styles.name, { fontSize: nameSize }]}>{name}</Text>
            </View>
            {fabLine ? (
              <Text style={styles.line} maxLines={1}>
                {fabLine}
              </Text>
            ) : null}
            {valLine ? (
              <Text style={[styles.line, styles.validity]} maxLines={1}>
                {valLine}
              </Text>
            ) : null}
          </View>
          {qr ? <Image src={qrImages.get(qr)} style={{ width: qrSize, height: qrSize }} /> : null}
        </View>
        {note ? (
          <Text style={styles.note} maxLines={2}>
            {note}
          </Text>
        ) : null}
      </>
    );
  };

  const doc = (
    <Document title="Etiquetas" creator="Cardápio Bonfim" producer="Cardápio Bonfim">
      {pages.map((slots, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page}>
          {slots.map((slot, index) => {
            // Posições puladas já foram usadas: nada é impresso sobre elas
            if (slot === "skipped" || (!slot && !layout.showBorders)) return null;
            const { left, top } = slotPosition(index, layout);
            return (
              <View
                key={index}
                style={[
                  styles.label,
                  { left: left * MM, top: top * MM },
                  layout.showBorders && styles.guide,
                ]}
              >
                {slot ? renderLabel(slot.label) : null}
              </View>
            );
          })}
        </Page>
      ))}
    </Document>
  );

  return pdf(doc).toBlob();
}
